import { branchRoutePath } from "./oauth/resource";
import { splitHost } from "./spaceDomain";

// The base domain that tenant subdomains hang off, e.g. "3b.run" for a wildcard
// of "cap.3b.run". tenants.public_domain stores only the tenant label ("cap");
// the base is universal across a stack's tenants and derived from the wildcard
// env value by dropping its first label. Empty when isolation is off (no
// wildcard) — callers then fall back to the bare public host.
export function publicBaseDomain(wildcardDomain: string | undefined): string {
  if (!wildcardDomain) return "";
  const dot = wildcardDomain.indexOf(".");
  return dot === -1 ? "" : wildcardDomain.slice(dot + 1);
}

export type TenantPublicUrlOptions = {
  publicUrl: string;
  publicWildcardDomain: string | undefined;
  combinedSpaceTenantLabel?: string;
};

export type PublicUrlStackConfig = {
  internalPublicUrl: string | undefined;
  maxTenants: number;
  publicWildcardDomain: string | undefined;
  combinedSpaceTenantHosts: boolean;
};

export function publicWildcardIsEnvironmentControlled(
  maxTenants: number,
  combinedSpaceTenantHosts: boolean
): boolean {
  return maxTenants === 1 && !combinedSpaceTenantHosts;
}

export function tenantPublicUrlOptionsFromOrigin(
  publicOrigin: string,
  config: Pick<
    PublicUrlStackConfig,
    "maxTenants" | "publicWildcardDomain" | "combinedSpaceTenantHosts"
  >
): TenantPublicUrlOptions {
  if (!config.publicWildcardDomain) {
    return {
      publicUrl: publicOrigin,
      publicWildcardDomain: undefined,
    };
  }
  const publicHostname = new URL(publicOrigin).hostname;
  if (config.combinedSpaceTenantHosts) {
    const split = splitHost(publicHostname);
    if (!split) {
      throw new Error(
        `Combined space-and-tenant hosts require a multi-label public origin: ${publicOrigin}`
      );
    }
    return {
      publicUrl: publicOrigin,
      publicWildcardDomain: split.parent,
      combinedSpaceTenantLabel: split.label,
    };
  }
  if (
    publicWildcardIsEnvironmentControlled(
      config.maxTenants,
      config.combinedSpaceTenantHosts
    )
  ) {
    return {
      publicUrl: publicOrigin,
      publicWildcardDomain: config.publicWildcardDomain,
    };
  }
  return {
    publicUrl: publicOrigin,
    publicWildcardDomain: publicHostname,
  };
}

export function withInternalPublicUrl(
  opts: TenantPublicUrlOptions,
  internalPublicUrl: string | undefined
): TenantPublicUrlOptions {
  if (!internalPublicUrl || opts.publicWildcardDomain) return opts;
  return { ...opts, publicUrl: internalPublicUrl };
}

type PublicWildcardConfig = {
  publicWildcardDomain?: string;
};

// Routes may repeat across spaces only when each space has its own public host.
export function publicRouteScope(
  config: PublicWildcardConfig
): "space" | "tenant" {
  return config.publicWildcardDomain ? "space" : "tenant";
}

export function spacePublicHostnameSuffix(
  opts: {
    publicWildcardDomain?: string;
    combinedSpaceTenantLabel?: string;
  },
  customDomain?: string | null
): string | null {
  if (customDomain) {
    return `.${customDomain}`;
  }
  if (opts.publicWildcardDomain) {
    return opts.combinedSpaceTenantLabel
      ? `--${opts.combinedSpaceTenantLabel}.${opts.publicWildcardDomain}`
      : `.${opts.publicWildcardDomain}`;
  }
  return null;
}

function spacePublicHostname(
  spaceSlug: string,
  opts: TenantPublicUrlOptions,
  customDomain?: string | null
): string | null {
  const suffix = spacePublicHostnameSuffix(opts, customDomain);
  return suffix ? `${spaceSlug}${suffix}` : null;
}

export function spacePublicOrigin(
  spaceSlug: string,
  opts: TenantPublicUrlOptions,
  customDomain?: string | null
): string {
  const hostname = spacePublicHostname(spaceSlug, opts, customDomain);
  if (!hostname) return opts.publicUrl;
  if (customDomain) return `https://${hostname}`;
  const origin = new URL(opts.publicUrl);
  origin.hostname = hostname;
  return origin.origin;
}

export function publicRouteUrl(
  origin: string,
  route: string,
  params?: { branchId?: string | null; externalId?: string | null }
): string {
  const path = params?.branchId
    ? branchRoutePath(params.branchId, route)
    : route;
  const base = `${origin}${path}`;
  const search = new URLSearchParams();
  if (params?.externalId) search.set("external_id", params.externalId);
  const qs = search.toString();
  return qs ? `${base}?${qs}` : base;
}
